import { MidiData } from 'midi-file';
import { getFirstNoteMidi } from '../../../common/src/midi_utils.ts';
import { Voice } from '../../../common/src/model.ts';
import { isIPhone } from '../util/platform.ts';
import { generateWavBlobForMIDI, MP3GeneratorResult } from './instrument.ts';

// TODO(hewitt): Share this functionality with vocals when we have access to the song MIDI data

// Single global audio element shared across all FirstNotePlayer instances.
// We only ever play one first note at a time, so there's no need for per-instance elements.
// This avoids DualAudioElement complexity (element swapping, synchronizeCurrentTime, manufactured
// canplaythrough events) that caused intermittent playback failures on iOS.
const audioElement = new Audio();
let prevObjectUrl: string | undefined;

function setAudioSrc(blob: Blob): Promise<void> {
  return new Promise<void>((resolve) => {
    const objectUrl = URL.createObjectURL(blob);
    if (prevObjectUrl) {
      URL.revokeObjectURL(prevObjectUrl);
    }
    prevObjectUrl = objectUrl;
    audioElement.src = objectUrl;
    if (isIPhone()) {
      // iPhone audio elements do not reliably fire canplaythrough
      audioElement.load();
      setTimeout(resolve, 250);
    } else {
      audioElement.addEventListener('canplaythrough', () => resolve(), { once: true });
    }
  });
}

export class FirstNotePlayer {
  private isPerformingMP3Refresh = false;
  private readonly getSongMidiData: () => MidiData | undefined;
  private readonly modulationHalfSteps: () => number;

  constructor({
    modulationHalfSteps,
    getSongMidiData,
  }: {
    modulationHalfSteps: () => number;
    getSongMidiData: () => MidiData | undefined;
  }) {
    this.modulationHalfSteps = modulationHalfSteps;
    this.getSongMidiData = getSongMidiData;
  }

  play(voice: Voice, startTick?: number) {
    this.pause();
    void (async () => {
      const songMidiData = this.getSongMidiData();
      if (this.isPerformingMP3Refresh || !songMidiData) {
        return;
      }
      this.isPerformingMP3Refresh = true;
      try {
        const midiData = getFirstNoteMidi(songMidiData, voice, startTick);
        if (!midiData) {
          return;
        }
        const inputs = {
          tempo: 100,
          modulationHalfSteps: this.modulationHalfSteps(),
          voiceValues: { [voice]: 1 },
        };
        const { audioBlob, result } = await generateWavBlobForMIDI(midiData, inputs, () => false);
        if (!audioBlob || result === MP3GeneratorResult.Failure) {
          return;
        }
        await setAudioSrc(audioBlob);
        audioElement.currentTime = 0;
        await audioElement.play();
      } catch (e) {
        console.error('FirstNotePlayer: failed to play', e);
      } finally {
        this.isPerformingMP3Refresh = false;
      }
    })();
  }

  pause() {
    audioElement.pause();
  }

  destroy() {
    this.pause();
  }
}
